import React, { useState, useEffect, useContext } from "react";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import { motion } from "framer-motion";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  FaCalendarAlt,
  FaStore,
  FaMoneyBillWave,
  FaBoxOpen,
  FaUsers,
  FaChartLine,
  FaChartPie,
  FaPrint,
} from "react-icons/fa";
import { fetchOrders } from "../../utils/api";
import AuthContext from "../../contexts/AuthContext";
import AnalyticsScreenModal from "../../components/AnalyticsScreenModal";

// Custom color palette
const colors = {
  primary: "#6366f1",
  secondary: "#8b5cf6",
  accent: "#ec4899",
  success: "#10b981",
  warning: "#f59e0b",
  danger: "#ef4444",
  dark: "#1e293b",
  light: "#f8fafc",
};

function PrintAnalyticsReport({
  orders,
  metrics,
  topItems,
  revenueByDay,
  ordersByDay,
  dateRange,
  recentOrders,
}) {
  const [open, setOpen] = useState(false);
  const [chartImages, setChartImages] = useState({});

  // Prepare report content as HTML (reuse the analytics-report div content)
  const reportContent = (
    <div>
      <div className="report-header">
        <h1>Restaurant Analytics Report</h1>
        <p className="date-range">
          Period: {dateRange[0] ? dateRange[0].toLocaleDateString() : "N/A"} -{" "}
          {dateRange[1] ? dateRange[1].toLocaleDateString() : "N/A"}
        </p>
      </div>
      <div className="section">
        <div className="section-title">Key Performance Metrics</div>
        <div className="metrics-grid">
          <div className="metric-card">
            <h3>Total Revenue</h3>
            <p>₹{metrics.totalRevenue.toFixed(2)}</p>
            <small>+12.5% from previous period</small>
          </div>
          <div className="metric-card">
            <h3>Total Orders</h3>
            <p>{metrics.totalOrders}</p>
            <small>+8.2% from previous period</small>
          </div>
          <div className="metric-card">
            <h3>Average Order Value</h3>
            <p>₹{metrics.avgOrderValue.toFixed(2)}</p>
            <small>+4.1% from previous period</small>
          </div>
          <div className="metric-card">
            <h3>Unique Customers</h3>
            <p>{metrics.uniqueCustomers}</p>
            <small>+15.3% from previous period</small>
          </div>
        </div>
      </div>
      <div className="section">
        <div className="section-title">Top Selling Items</div>
        <table>
          <thead>
            <tr>
              <th>Rank</th>
              <th>Item Name</th>
              <th>Quantity Sold</th>
              <th>Revenue Generated</th>
            </tr>
          </thead>
          <tbody>
            {topItems.map(([item, qty], index) => (
              <tr key={item}>
                <td>{index + 1}</td>
                <td>{item}</td>
                <td>{qty}</td>
                <td>
                  ₹
                  {(
                    qty *
                      orders
                        .find((o) => o.items.find((i) => i.name === item))
                        ?.items.find((i) => i.name === item)?.price || 0
                  ).toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="section">
        <div className="section-title">Daily Revenue Breakdown</div>
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Revenue</th>
              <th>Orders</th>
              <th>Avg. Order Value</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(revenueByDay).map(([date, revenue]) => (
              <tr key={date}>
                <td>{date}</td>
                <td>₹{revenue.toFixed(2)}</td>
                <td>{ordersByDay[date] || 0}</td>
                <td>₹{(revenue / (ordersByDay[date] || 1)).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="section">
        <div className="section-title">Order Status Summary</div>
        <table>
          <thead>
            <tr>
              <th>Status</th>
              <th>Count</th>
              <th>Revenue</th>
              <th>% of Total Orders</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(
              orders.reduce((acc, order) => {
                const status = order.status;
                if (!acc[status]) acc[status] = { count: 0, revenue: 0 };
                acc[status].count++;
                acc[status].revenue += order.total * 1.05;
                return acc;
              }, {})
            ).map(([status, data]) => (
              <tr key={status}>
                <td>{status}</td>
                <td>{data.count}</td>
                <td>₹{data.revenue.toFixed(2)}</td>
                <td>{((data.count / orders.length) * 100).toFixed(1)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="section">
        <div className="section-title">Recent Orders</div>
        <table>
          <thead>
            <tr>
              <th>Order ID</th>
              <th>Date & Time</th>
              <th>Table</th>
              <th>Items</th>
              <th>Total</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {recentOrders.map((order) => (
              <tr key={order.id}>
                <td>#{order.id.slice(-5)}</td>
                <td>{safeDateString(order.createdAt)}</td>
                <td>{order.tableName || `Table ${order.tableId}`}</td>
                <td>
                  {Array.isArray(order.items)
                    ? order.items.map((i) => `${i.name} (${i.qty})`).join(", ")
                    : ""}
                </td>
                <td>₹{(order.total * 1.05).toFixed(2)}</td>
                <td>{order.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // Function to capture chart images
  const captureCharts = () => {
    const revenueChart = document
      .getElementById("revenue-trend-chart")
      ?.querySelector("canvas");
    const topItemsChart = document
      .getElementById("top-items-chart")
      ?.querySelector("canvas");
    const orderVolumeChart = document
      .getElementById("order-volume-chart")
      ?.querySelector("canvas");
    setChartImages({
      revenue: revenueChart ? revenueChart.toDataURL("image/png") : null,
      topItems: topItemsChart ? topItemsChart.toDataURL("image/png") : null,
      orderVolume: orderVolumeChart
        ? orderVolumeChart.toDataURL("image/png")
        : null,
    });
  };

  // Open modal and capture charts
  const openModal = () => {
    captureCharts();
    setOpen(true);
  };

  return (
    <>
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={openModal}
        className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-5 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all flex items-center gap-2 w-full sm:w-auto justify-center"
      >
        <FaPrint />
        Print Report
      </motion.button>
      <AnalyticsScreenModal
        open={open}
        onClose={() => setOpen(false)}
        reportContent={reportContent}
        chartImages={chartImages}
      />
    </>
  );
}

function safeDateString(ts) {
  if (!ts) return "-";
  if (typeof ts === "string" || typeof ts === "number") {
    try {
      return new Date(ts).toLocaleDateString();
    } catch {
      return "-";
    }
  }
  if (typeof ts._seconds === "number") {
    try {
      return new Date(ts._seconds * 1000).toLocaleDateString();
    } catch {
      return "-";
    }
  }
  return "-";
}

function AnalyticsErrorBoundary({ children }) {
  const [error, setError] = useState(null);
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-purple-50">
        <div className="bg-white p-8 rounded-2xl shadow-2xl text-center max-w-md">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Something went wrong
          </h2>
          <p className="text-gray-600 mb-6">
            {error.message || "An error occurred while rendering analytics."}
          </p>
        </div>
      </div>
    );
  }
  return React.cloneElement(children, { onError: setError });
}

export default function AnalyticsScreen() {
  const { restaurantId } = useContext(AuthContext);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState([
    new Date(new Date().setDate(new Date().getDate() - 30)),
    new Date(),
  ]);
  const [startDate, endDate] = dateRange;
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    if (restaurantId) {
      setLoading(true);
      setError(null);
      const isValidDate = (d) => d instanceof Date && !isNaN(d);
      const start = isValidDate(startDate) ? startDate.toISOString() : "";
      const end = isValidDate(endDate) ? endDate.toISOString() : "";
      if (start && end) {
        fetchOrders(restaurantId, start, end)
          .then((data) => {
            setOrders(Array.isArray(data) ? data : []);
            setLoading(false);
          })
          .catch((err) => {
            console.error("Error fetching analytics data:", err);
            setError("Could not load analytics data. Please try again.");
            setLoading(false);
          });
      } else {
        setOrders([]);
        setLoading(false);
      }
    }
  }, [restaurantId, dateRange]);

  // Calculate metrics
  const totalRevenue = orders.reduce((sum, o) => sum + o.total * 1.05, 0);
  // ...
  const totalOrders = orders.length;
  const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;
  const uniqueCustomers = new Set(orders.map((o) => o.tableId)).size;

  // Defensive: always use array for orders
  const safeOrders = Array.isArray(orders) ? orders : [];

  // Defensive: always use array for items
  const topSellingItems = safeOrders
    .flatMap((o) => (Array.isArray(o.items) ? o.items : []))
    .reduce((acc, item) => {
      if (!item || !item.name) return acc;
      acc[item.name] = (acc[item.name] || 0) + (item.qty || 0);
      return acc;
    }, {});

  const topItems = Object.entries(topSellingItems)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  // Defensive: createdAt
  const revenueByDay = safeOrders.reduce((acc, o) => {
    const date =
      o.createdAt && typeof o.createdAt._seconds === "number"
        ? new Date(o.createdAt._seconds * 1000).toLocaleDateString()
        : null;
    if (date) acc[date] = (acc[date] || 0) + o.total * 1.05;
    return acc;
  }, {});

  const revenueTrendData = {
    labels: Object.keys(revenueByDay),
    datasets: [
      {
        label: "Daily Revenue",
        data: Object.values(revenueByDay),
        fill: true,
        backgroundColor: "rgba(99, 102, 241, 0.1)",
        borderColor: colors.primary,
        borderWidth: 3,
        pointBackgroundColor: colors.primary,
        pointBorderColor: "#fff",
        pointRadius: 5,
        pointHoverRadius: 7,
        tension: 0.4,
      },
    ],
  };

  // Defensive: createdAt
  const ordersByDay = safeOrders.reduce((acc, o) => {
    const date =
      o.createdAt && typeof o.createdAt._seconds === "number"
        ? new Date(o.createdAt._seconds * 1000).toLocaleDateString()
        : null;
    if (date) acc[date] = (acc[date] || 0) + 1;
    return acc;
  }, {});

  const orderVolumeData = {
    labels: Object.keys(ordersByDay),
    datasets: [
      {
        label: "Orders per Day",
        data: Object.values(ordersByDay),
        backgroundColor: "rgba(139, 92, 246, 0.7)",
        borderColor: colors.secondary,
        borderWidth: 2,
        borderRadius: 4,
      },
    ],
  };

  // Defensive: recent orders
  const recentOrders = safeOrders
    .filter((o) => Array.isArray(o.items))
    .reverse()
    .slice(0, 10);

  const topItemsData = {
    labels: topItems.map(([name]) => name),
    datasets: [
      {
        label: "Quantity Sold",
        data: topItems.map(([_, qty]) => qty),
        backgroundColor: [
          "rgba(99, 102, 241, 0.8)",
          "rgba(139, 92, 246, 0.8)",
          "rgba(236, 72, 153, 0.8)",
          "rgba(16, 185, 129, 0.8)",
          "rgba(245, 158, 11, 0.8)",
        ],
        borderColor: "#fff",
        borderWidth: 2,
      },
    ],
  };

  // Empty state helpers
  const noOrders = orders.length === 0;
  const noTopItems = topItems.length === 0;
  const noRevenue = Object.keys(revenueByDay).length === 0;
  const noOrderVolume = Object.keys(ordersByDay).length === 0;

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-purple-50">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="w-16 h-16 rounded-full border-4 border-indigo-500 border-t-transparent"
        />
      </div>
    );

  if (error)
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-purple-50">
        <div className="bg-white p-8 rounded-2xl shadow-2xl text-center max-w-md">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Error Loading Data
          </h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-indigo-600 text-white px-6 py-3 rounded-full shadow-lg hover:bg-indigo-700 transition-all"
          >
            Try Again
          </button>
        </div>
      </div>
    );

  return (
    <AnalyticsErrorBoundary>
      <div className="min-h-full bg-gradient-to-br from-indigo-50 to-purple-50 p-4 md:p-8 rounded-2xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-7xl mx-auto space-y-8"
        >
          {/* Header */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">
                Restaurant Analytics
              </h1>
              <p className="text-gray-600 mt-1">
                Insights to grow your business
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full md:w-auto">
              <div className="relative w-full sm:w-64">
                <DatePicker
                  selectsRange={true}
                  startDate={startDate}
                  endDate={endDate}
                  onChange={(update) => setDateRange(update)}
                  className="bg-white pl-10 pr-4 py-3 rounded-xl border border-gray-200 shadow-sm w-full focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  dateFormat="MMM d, yyyy"
                  maxDate={new Date()}
                />
                <FaCalendarAlt className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              </div>
              <PrintAnalyticsReport
                orders={orders}
                metrics={{
                  totalRevenue,
                  totalOrders,
                  avgOrderValue,
                  uniqueCustomers,
                }}
                topItems={topItems}
                revenueByDay={revenueByDay}
                ordersByDay={ordersByDay}
                dateRange={dateRange}
                recentOrders={recentOrders}
              />
            </div>
          </div>
          {/* Navigation Tabs */}
          <div className="flex border-b border-gray-200">
            {["overview"].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-3 font-medium text-sm capitalize relative ${
                  activeTab === tab
                    ? "text-indigo-600"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {tab}
                {activeTab === tab && (
                  <motion.div
                    layoutId="underline"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600"
                    transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                  />
                )}
              </button>
            ))}
          </div>
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard
              icon={<FaMoneyBillWave className="text-indigo-500" />}
              title="Total Revenue"
              value={noOrders ? "₹0.00" : `₹${totalRevenue.toFixed(2)}`}
              change={noOrders ? "" : "+12.5%"}
              color="indigo"
            />
            <StatCard
              icon={<FaBoxOpen className="text-purple-500" />}
              title="Total Orders"
              value={noOrders ? "0" : totalOrders}
              change={noOrders ? "" : "+8.2%"}
              color="purple"
            />
            <StatCard
              icon={<FaStore className="text-pink-500" />}
              title="Avg. Order Value"
              value={noOrders ? "₹0.00" : `₹${avgOrderValue.toFixed(2)}`}
              change={noOrders ? "" : "+4.1%"}
              color="pink"
            />
            <StatCard
              icon={<FaUsers className="text-emerald-500" />}
              title="Unique Customers"
              value={noOrders ? "0" : uniqueCustomers}
              change={noOrders ? "" : "+15.3%"}
              color="emerald"
            />
          </div>
          {/* Main Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              id="revenue-trend-chart"
              className="lg:col-span-2 bg-white p-6 rounded-2xl shadow-lg border border-gray-100"
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                  <FaChartLine className="text-indigo-500" />
                  Revenue Trend
                </h2>
              </div>
              <div className="h-80 flex items-center justify-center">
                {noRevenue ? (
                  <div className="text-gray-400 text-lg">
                    No revenue data for selected date range
                  </div>
                ) : (
                  <Line
                    data={revenueTrendData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { display: false },
                        tooltip: {
                          backgroundColor: colors.dark,
                          titleFont: { size: 14 },
                          bodyFont: { size: 12 },
                          padding: 10,
                          usePointStyle: true,
                          callbacks: {
                            label: (context) => ` ₹${context.raw.toFixed(2)}`,
                          },
                        },
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          grid: { color: "rgba(0, 0, 0, 0.05)" },
                          ticks: { callback: (value) => `₹${value}` },
                        },
                        x: { grid: { display: false } },
                      },
                    }}
                  />
                )}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              id="top-items-chart"
              className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100"
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                  <FaChartPie className="text-purple-500" />
                  Top Selling Items
                </h2>
              </div>
              <div className="h-80 flex items-center justify-center">
                {noTopItems ? (
                  <div className="text-gray-400 text-lg">
                    No top selling items for selected date range
                  </div>
                ) : (
                  <Doughnut
                    data={topItemsData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      cutout: "70%",
                      plugins: {
                        legend: {
                          position: "right",
                          labels: {
                            usePointStyle: true,
                            padding: 20,
                            font: { size: 12 },
                          },
                        },
                        tooltip: {
                          backgroundColor: colors.dark,
                          bodyFont: { size: 12 },
                          padding: 10,
                          usePointStyle: true,
                        },
                      },
                    }}
                  />
                )}
              </div>
            </motion.div>
          </div>
          {/* Additional Charts */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            id="order-volume-chart"
            className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100"
          >
            <h2 className="text-xl font-semibold text-gray-800 mb-6">
              Order Volume
            </h2>
            <div className="h-80 flex items-center justify-center">
              {noOrderVolume ? (
                <div className="text-gray-400 text-lg">
                  No order volume data for selected date range
                </div>
              ) : (
                <Bar
                  data={orderVolumeData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        backgroundColor: colors.dark,
                        titleFont: { size: 14 },
                        bodyFont: { size: 12 },
                        padding: 10,
                        usePointStyle: true,
                      },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        grid: { color: "rgba(0, 0, 0, 0.05)" },
                      },
                      x: { grid: { display: false } },
                    },
                  }}
                />
              )}
            </div>
          </motion.div>
          {/* Top Items List */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="flex flex-col items-center gap-6"
          >
            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 w-full max-w-xl mx-auto">
              <h2 className="text-xl font-semibold text-gray-800 mb-6 text-center">
                Top Selling Items
              </h2>
              <div className="space-y-4">
                {noTopItems ? (
                  <div className="text-gray-400 text-center py-8">
                    No top selling items for selected date range
                  </div>
                ) : (
                  topItems.map(([item, qty], index) => (
                    <div
                      key={item}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center text-white ${
                            index === 0
                              ? "bg-amber-400"
                              : index === 1
                              ? "bg-gray-400"
                              : index === 2
                              ? "bg-amber-600"
                              : "bg-gray-300"
                          }`}
                        >
                          {index + 1}
                        </div>
                        <span className="font-medium">{item}</span>
                      </div>
                      <span className="font-semibold">{qty} sold</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </AnalyticsErrorBoundary>
  );
}

function StatCard({ icon, title, value, change, color }) {
  const colorClasses = {
    indigo: "bg-indigo-50 border-indigo-100",
    purple: "bg-purple-50 border-purple-100",
    pink: "bg-pink-50 border-pink-100",
    emerald: "bg-emerald-50 border-emerald-100",
  };

  const changeColors = {
    positive: "text-emerald-600 bg-emerald-50",
    negative: "text-red-600 bg-red-50",
    neutral: "text-gray-600 bg-gray-50",
  };

  const getChangeColor = (change) => {
    if (!change) return changeColors.neutral;
    return change.startsWith("+")
      ? changeColors.positive
      : change.startsWith("-")
      ? changeColors.negative
      : changeColors.neutral;
  };

  return (
    <motion.div
      whileHover={{ y: -5 }}
      className={`p-5 rounded-xl border ${colorClasses[color]} shadow-sm transition-all`}
    >
      <div className="flex items-center justify-between">
        <div className="p-3 rounded-lg bg-white shadow-xs">{icon}</div>
        {change && (
          <span
            className={`text-xs px-2 py-1 rounded-full ${getChangeColor(
              change
            )}`}
          >
            {change}
          </span>
        )}
      </div>
      <div className="mt-4">
        <p className="text-sm font-medium text-gray-500">{title}</p>
        <p className="text-2xl font-bold text-gray-800 mt-1">{value}</p>
      </div>
    </motion.div>
  );
}
